import { Navigate } from 'react-router-dom'

import { useAuth } from '../../_hooks/use-auth'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { useExperience } from '../../_hooks/use-experience'

/**
 * Renders at the root path (`/`). Decides where the user lands, honouring the
 * RBC two-lands model:
 *   - signed out → /sign-in
 *   - resident land (has a tenancy, or last chose it) → /my-home
 *   - search land, onboarding incomplete → /onboarding
 *   - search land, onboarded → /dashboard
 *
 * The active land comes from ExperienceProvider, which only resolves once the
 * profile + tenancy signals have settled — so `isResolved` is the single gate
 * we wait on (it subsumes the profile/tenancy loading checks).
 *
 * No public landing page exists on app.quickpass.club — marketing landing
 * lives at the bare quickpass.club domain in a separate codebase.
 *
 * @returns {JSX.Element | null}
 */
const AuthGate = (): JSX.Element | null => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const { hasFinishedOnboarding } = useQuickPassProfile()
  const { mode, isResolved } = useExperience()

  if (isAuthLoading === true) {
    return null
  }

  if (isAuthenticated === false) {
    return <Navigate to="/sign-in" replace />
  }

  if (isResolved === false) {
    return null
  }

  if (mode === 'resident') {
    return <Navigate to="/my-home" replace />
  }

  if (hasFinishedOnboarding === false) {
    return <Navigate to="/onboarding" replace />
  }

  return <Navigate to="/dashboard" replace />
}

export default AuthGate
